import { formatInTimeZone } from "../../helpers/timeZone";
import { MediaEnum } from "./enums";
import { isIosDevice } from "./helpers";
import type {
  DownloadOptionsType,
  PhotoItemType,
  VideoItemType,
} from "./types";
import { toastMessage } from "../toast";
import { downloadMedia, downloadMediaForSession } from "../../services";
import axios from "axios";
import { type ZipDownloaderProps } from "./ZipDownloader";

export type DownloadMediaItemsProgress = {
  progress: number;
  fileName?: string;
  starting?: boolean;
  completed?: boolean;
  failedCount?: number;
};

export type DownloadMediaItemsCallback = (
  progress: DownloadMediaItemsProgress
) => void;

export type ZipBuildCallback = (props: ZipDownloaderProps) => void;

/**
 * Pause between two saved files. iOS Safari hands each download over to its own
 * download manager and drops a second save that arrives while the first is still
 * being processed, so the files have to be spaced out.
 */
const SAVE_HANDOFF_DELAY_MS = 800;

const delay = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Hands a downloaded blob to the browser. The anchor has to be in the document
 * for the click to be honoured on mobile Safari, and the object URL is released
 * afterwards so large galleries do not pin every file in memory.
 */
function saveBlob(data: BlobPart, fileName: string) {
  const blobUrl = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoking straight away can cancel the save while the browser is still
  // reading the blob, so give it a moment first.
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
}

export function downloadMediaItems(
  itemsToDownload: (VideoItemType | PhotoItemType)[],
  callback?: DownloadMediaItemsCallback,
  timeZone?: string
) {
  function getTargetFileName(item: VideoItemType | PhotoItemType): string {
    function getDatePrefix(dateTime: string) {
      return formatInTimeZone(dateTime, "YYYY-MM-DD_HH-mm-ss", timeZone);
    }

    if ((item as VideoItemType).recordDate != undefined) {
      // Video
      const videoItem = item as VideoItemType;
      return `${getDatePrefix(videoItem.recordDate)}_${videoItem.name}`;
    } else {
      // Photo
      const photoItem = item as PhotoItemType;
      return `${getDatePrefix(photoItem.takenOnUtc)}_${photoItem.name}`;
    }
  }

  const totalCount = itemsToDownload.length;

  const downloadFile = async (
    fileUrl: string,
    fileName: string,
    doneCount: number
  ): Promise<boolean> => {
    try {
      const resp = await axios.get(fileUrl, {
        responseType: "blob",
        onDownloadProgress: (progressEvent) => {
          const fileProgress = progressEvent.progress ?? 0;
          callback?.({
            fileName,
            progress: ((doneCount + fileProgress) / totalCount) * 100,
          });
        },
      });

      saveBlob(resp.data, fileName);
      return true;
    } catch (error) {
      toastMessage.error(`Failed to download ${fileName}: ${error}`, Infinity);
      return false;
    }
  };

  const run = async () => {
    callback?.({ progress: 0, starting: true });

    let failedCount = 0;

    // Strictly sequential: starting a save aborts the requests still in flight
    // on iOS Safari, which used to fail every file but the first one with
    // "AxiosError: Network Error".
    for (let index = 0; index < itemsToDownload.length; index++) {
      const item = itemsToDownload[index];

      if (!item.url) {
        toastMessage.error(`No url found ${item.name}`, Infinity);
        failedCount++;
        continue;
      }

      const succeeded = await downloadFile(
        item.url,
        getTargetFileName(item),
        index
      );
      if (!succeeded) {
        failedCount++;
      }

      callback?.({ progress: ((index + 1) / totalCount) * 100 });

      if (index < itemsToDownload.length - 1) {
        await delay(SAVE_HANDOFF_DELAY_MS);
      }
    }

    callback?.({
      progress: 100,
      completed: true,
      failedCount,
    });
  };

  void run();
}

export const handleDownloadAsZip = (
  reservationId: string | null,
  photoSessionId: string | null,
  setIsLoading: (value: React.SetStateAction<boolean>) => void,
  itemsToDownload: (VideoItemType | PhotoItemType)[],
  onZipCreationStarted: ZipBuildCallback,
  callback?: DownloadMediaItemsCallback,
  guestToken?: string | null
) => {
  const { photoIds, videoIds } = itemsToDownload.reduce(
    (acc, item: VideoItemType | PhotoItemType) => {
      if (item.mediaType === MediaEnum.Photo) {
        acc.photoIds.push(item.id);
      } else {
        acc.videoIds.push(item.id);
      }
      return acc;
    },
    { photoIds: [] as string[], videoIds: [] as string[] }
  );

  const downloadFunc = async (): Promise<ZipDownloaderProps | null> => {
    if (reservationId) {
      const zipId = await downloadMedia(reservationId, photoIds, videoIds);
      if (zipId != null) {
        return { zipId, fileName: reservationId, callback, guestToken };
      }
    } else if (photoSessionId) {
      const zipId = await downloadMediaForSession(
        photoSessionId,
        photoIds,
        videoIds
      );
      if (zipId != null) {
        return { zipId, fileName: photoSessionId, callback, guestToken };
      }
    }

    return null;
  };

  callback?.({ progress: 0, starting: true });
  setIsLoading(true);

  downloadFunc()
    .then((res) => {
      if (res == null) {
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      onZipCreationStarted({
        zipId: res.zipId,
        fileName: res.fileName,
        callback,
        guestToken,
      });
    })
    .catch((err) => {
      toastMessage.error("Error!");
      console.error(err);
      setIsLoading(false);
    });
};

export const handleDownload = (
  filteredItems: (VideoItemType | PhotoItemType)[],
  selectedItems: string[],
  setDownloadableItems: (
    value: React.SetStateAction<(VideoItemType | PhotoItemType)[]>
  ) => void,
  downloadOptions: DownloadOptionsType,
  downloadDialogRef: React.MutableRefObject<HTMLDialogElement | null>,
  photoSessionId: string | null,
  downloadCallback: DownloadMediaItemsCallback | undefined,
  zipBuildCallback: ZipBuildCallback,
  reservationId: string | null,
  setIsLoading: (value: React.SetStateAction<boolean>) => void,
  downloadAll: boolean,
  guestToken?: string | null,
  timeZone?: string
) => {
  const itemsToDownload = downloadAll
    ? filteredItems
    : filteredItems.filter((item) => selectedItems.includes(item.id));

  setDownloadableItems(itemsToDownload);

  const startZip = () =>
    // No downloadCallback here on purpose: the zip path reports progress
    // through ZipDownloader, and passing it would start a progress indicator
    // that nothing ever completes.
    handleDownloadAsZip(
      reservationId,
      photoSessionId,
      setIsLoading,
      itemsToDownload,
      zipBuildCallback,
      undefined,
      guestToken
    );

  // iOS can only ever save one file per user gesture, so several separate
  // files is not an option there - go straight to the zip instead of offering
  // a choice that cannot work. A single file still downloads directly.
  if (
    isIosDevice() &&
    itemsToDownload.length > 1 &&
    (downloadOptions.zip || !downloadOptions.separated)
  ) {
    startZip();
    return;
  }

  if (downloadOptions.separated && downloadOptions.zip) {
    downloadDialogRef.current?.showModal();
    return;
  }

  if (downloadOptions.separated) {
    downloadMediaItems(itemsToDownload, downloadCallback, timeZone);
  } else {
    startZip();
  }
};
